<template lang="pug">
#reports
	.title-container
		h1.title Reports
		.download-dropdown
			p.spacey
				span Download as:
				span.select  {{ downloadType }}
			dropdown-filter(
				@filter='downloadType = $event'
				:options='dropdownOptions'
			)
	.content
		.report-lists
			.column
				report-list(
					:title='"Recently Pulled"'
					:list='recentlyPulled'
					@open-filter='openNewFilterModal'
					@download='download'
					@last-click='lastClickedItem = $event'
					v-show='recentlyPulled.length'
				)
				report-list.custom(
					:title='"Generate Custom Reports"'
					:list='customReports'
					@open-filter='openNewFilterModal'
					@download='download'
					@last-click='lastClickedItem = $event'
				)
			report-list(
				v-for='( list, listId ) in gisReports'
				:key='listId'
				:title='list.title'
				:list='list.items'
				@download='download'
				@last-click='lastClickedItem = $event'
			)
	new-filters-modal(
		:state='newFiltersState'
		:config='activeConfig'

		@close='closeNewFilterModal'
		@download='downloadWithNewFilters'
	)

</template>

<script>
import NewFiltersModal from '@/components/modal/NewFiltersModal';
import ReportList from '@/components/ReportList';

import ActionRequestsConfig from '@/lib/assets/actionRequests';
import AgreementsConfig from '@/lib/assets/agreements';
import ReviewsConfig from '@/lib/assets/devUtilReviews';
import FacilitiesConfig from '@/lib/assets/facilities';
import JobsConfig from '@/lib/assets/jobs';
import Permits from '@/lib/assets/permits';

import DropdownFilter from '@/components/DropdownFilter';

import {
	DownloadAgreementsCSV,
	DownloadAgreementsPDF,
	DownloadFacilitiesCSV,
	DownloadFacilitiesPDF,
	DownloadReviewsCSV,
	DownloadReviewsPDF,
	DownloadRequestsCSV,
	DownloadRequestsPDF,
	DownloadJobsCSV,
	DownloadJobsPDF,
	DownloadPermitsCSV,
	DownloadPermitsPDF,
	DownloadReport,
	ImportGis,
} from '@/lib/API';

import { GetRecentReports, AddRecentReport } from '@/lib/db/reports';

import { FilterToAPI } from '@/lib/utils';

export default {
	name : 'reports',

	data : () => ( {
		newFiltersState : 'closed',

		activeAssetFilter : '',

		recentlyPulled : [],

		customReports : [
			{
				text  : 'Agreements',
				type  : 'filter',
				value : 'agreements'
			},
			{
				text  : 'Developer & Utility Reviews',
				type  : 'filter',
				value : 'reviews'
			},
			{
				text  : 'Engineering Action Requests',
				type  : 'filter',
				value : 'actionRequests'
			},
			{
				text  : 'Facility Information',
				type  : 'filter',
				value : 'facilities'
			},
			{
				text  : 'Job Information',
				type  : 'filter',
				value : 'jobs'
			},
			// {
			// 	text  : 'Parcel Look-up',
			// 	type  : 'filter',
			// 	value : 'parcels'
			// },
			{
				text  : 'Permits',
				type  : 'filter',
				value : 'permits'
			}
		],

		gisReports : {
			turnoutInfo : {
				title : 'Turnout Information',
				items : {
					tocByFacAndStrucType : {
						text  : 'Turnout Count by Facility & Structure Type',
						type  : 'download',
						value : 'turnout-ct-by-facility-structure'
					},
					tocByFacAndTOType : {
						text  : 'Turnout Count by Facility & T.O. Type',
						type  : 'download',
						value : 'turnout-ct-by-facility-TO'
					},
					tocByCustomerAndMeasurement : {
						text  : 'Turnout Count by Customer T.O. Type & Measurement Type',
						type  : 'download',
						value : 'turnout-ct-by-customer-TO-measurement'
					},
					tocByStrucType : {
						text  : 'Turnout Count by Structure Type',
						type  : 'download',
						value : 'turnout-ct-by-structure'
					},
					tocByTOType : {
						text  : 'Turnout Count by T.O. Type',
						type  : 'download',
						value : 'turnout-ct-by-TO-type'
					},
					tocByWSOAndTOType : {
						text  : 'Turnout Count by WSO Area & T.O. Type',
						type  : 'download',
						value : 'turnout-ct-by-wso-area-TO-type'
					},
					tocByWSOAreaAndFac : {
						text  : 'Turnout Count by WSO Area & Facility',
						type  : 'download',
						value : 'turnout-ct-by-wso-area-facility'
					},
				},
			},

			structures : {
				title : 'Structures',
				items : {
					autoTrashRackList : {
						text  : 'Automatic Trash Rack List',
						type  : 'download',
						value : 'automatic-trashracks'
					},
					fidPipeLinesWRisers : {
						text  : 'FID Pipelines W/ Risers',
						type  : 'download',
						value : 'pipelines'
					},
					flowMeasureListAllSites : {
						text  : 'Flow Measure List, All Sites',
						type  : 'download',
						value : 'flow-meters-all'
					},
					flowMeasureListFIDNonProps : {
						text  : 'Flow Measure List, FID Non-Props',
						type  : 'download',
						value : 'flow-meters-non-props'
					},
					flowMeasureListFIDProps : {
						text  : 'Flow Measure List, FID Props',
						type  : 'download',
						value : 'flow-meters-props'
					},
					pgeListByFacilityNo : {
						text  : 'PG&E List, By Facility No.',
						type  : 'download',
						value : 'pge-facility-number'
					},
					pgeListByMeterNo : {
						text  : 'PG&E List, By Meter No.',
						type  : 'download',
						value : 'pge-meter-number'
					},
					rechargeAndDischargeIntertieList : {
						text  : 'Recharge & Discharge Intertie List',
						type  : 'download',
						value : 'recharge-discharge-intertie'
					},
					telemtrySiteListByNumber : {
						text  : 'Telemetry Site List, By Number',
						type  : 'download',
						value : 'telemetry-site-by-number'
					},
					weirListFlapGates : {
						text  : 'Weir List, Flap Gates',
						type  : 'download',
						value : 'weir-flap-gates'
					},
					weirListLangemannGates : {
						text  : 'Weir List, Langemann Gates',
						type  : 'download',
						value : 'weir-langemann-gate'
					},
					weirListLongCrested : {
						text  : 'Weir List, Long-Crested',
						type  : 'download',
						value : 'weir-long-crested'
					}
				},
			}
		},

		lastClickedItem : null,
		downloadType    : 'CSV',

		recentReportsListener : null,

		dropdownOptions : {
			options : ['csv', 'pdf'].map( a => ( {
				value : a,
				text  : a.toUpperCase(),
			} ) ),
			alwaysShowArrow : true,
		},

		state : 'idle',
	} ),

	computed : {

		user() {
			return this.$store.state.user;
		},

		activeConfig() {
			const configs = {
				agreements     : AgreementsConfig,
				reviews        : ReviewsConfig,
				actionRequests : ActionRequestsConfig,
				facilities     : FacilitiesConfig,
				jobs           : JobsConfig,
				permits        : Permits,
				// parcels        : ParcelsConfig,
			};

			const activeConfig = configs[this.activeAssetFilter];

			if ( !activeConfig ) {
				console.warn( `There is no config file for asset ${this.activeAssetFilter}` );
				// console.log( this.activeAssetFilter );
				return {
					columns         : {},
					dropdownOptions : {},
				};
			}

			return activeConfig;

		},

		downloadMethods() {
			const downloads = {
				agreements : {
					name : 'agreements',
					PDF  : DownloadAgreementsPDF,
					CSV  : DownloadAgreementsCSV,
				},
				reviews : {
					name : 'reviews',
					PDF  : DownloadReviewsPDF,
					CSV  : DownloadReviewsCSV,
				},
				actionRequests : {
					name : 'actionRequests',
					PDF  : DownloadRequestsPDF,
					CSV  : DownloadRequestsCSV,
				},
				facilities : {
					name : 'facilities',
					PDF  : DownloadFacilitiesPDF,
					CSV  : DownloadFacilitiesCSV,
				},
				jobs : {
					name : 'jobs',
					PDF  : DownloadJobsPDF,
					CSV  : DownloadJobsCSV,
				},
				permits : {
					name : 'permits',
					PDF  : DownloadPermitsPDF,
					CSV  : DownloadPermitsCSV,
				},
				// parcels : {
				// 	name : 'parcels',
				// 	PDF  : DownloadParcelsPDF,
				// 	CSV  : DownloadParcelsCSV,
				// }
			};

			const download = downloads[this.activeAssetFilter];

			// console.log( this.activeAssetFilter );
			// console.log( download );

			if ( !download ) {
				console.warn( `No download methods for asset ${this.activeAssetFilter}` );
				// console.log( this.activeAssetFilter );
				return {
					PDF : () => {},
					CSV : () => {}
				};
			}

			return download;
		}
	},

	watch : {
		user : {
			immediate : true,
			handler( user ) {

				if ( !user || !user.uid ) {
					return;
				}

				if ( this.recentReportsListener ) {
					this.recentReportsListener();
				}

				const { uid } = user;

				this.recentReportsListener = GetRecentReports( uid, ( reports ) => {
					this.recentlyPulled = reports;
				} );
			}
		}
	},

	methods : {
		openNewFilterModal( assetName ) {
			this.newFiltersState   = 'opened';
			this.activeAssetFilter = assetName;
		},

		closeNewFilterModal() {
			this.newFiltersState = 'closed';
		},

		downloadWithNewFilters( filters ) {
			const { uid } = this.user;
			// eslint-disable-next-line object-curly-newline
			const { lastClickedItem, downloadMethods, downloadType, activeConfig } = this;
			const { columns } = activeConfig;
			const formmatedFilters = FilterToAPI( filters );

			const formattedColumns = Object.keys( columns )
				.map( a => columns[a].filterColumn || a );

			downloadMethods[downloadType]( formmatedFilters, formattedColumns )
				.then( () => {
					AddRecentReport( uid, lastClickedItem );
				} )
				.catch( err => console.warn( err ) );

		},

		download( { value, text } ) {

			if ( this.state === 'loading' ) {
				return;
			}

			const { uid } = this.user;
			const { lastClickedItem } = this;

			const displayStateOptions = {
				message        : 'Importing GIS Data',
				successMessage : 'Import Successful',
				failMessage    : 'Import failed',
				state          : 'loading',
			};

			this.state = 'loading';

			this.$store.dispatch( 'loadWithDisplayState', {
				options : displayStateOptions,
				method  : () => ImportGis(),
			} )
				.then( () => {

					const options = {
						message        : 'Downloading Report',
						successMessage : 'Download Successful',
						failMessage    : 'Download failed',
						state          : 'loading',
					};

					return this.$store.dispatch( 'loadWithDisplayState', {
						options,
						method : () => DownloadReport( value, this.downloadType, text )
							.then( () => AddRecentReport( uid, lastClickedItem ) )
					} );

				} )
				.then( () => {
					this.state = 'idle';
				} )
				.catch( ( err ) => {
					console.warn( err );
					this.state = 'idle';
				} );
		},

		// addToRecentlyPulled( ...args ) {
		// 	// console.log( args );
		// }

	},

	components : {
		NewFiltersModal,
		ReportList,
		DropdownFilter,
	}

};
</script>

<style lang="scss">
#reports {
	width: 100%;
	height: 100vh;
	overflow-y: auto;

	> .title-container {
		margin: 25px 60px;

		.title {
			color: $input-color;
		}

		.download-dropdown {
			position: relative;
			display: flex;

			.dropdown-filter {
				margin-left: 10px;

				.content {
					margin: 0;

					.option {
						width: 50px;
						min-width: unset;
					}
				}
			}
		}
	}

	.content {
		margin: 0px 25px;

		.report-lists {
			display: flex;

			.column {
				flex : 1 1 0;
			}

			.report-list.custom {

				.list-item.unavailable {
					opacity: 1;
				}
			}
		}
	}
}
</style>
